// menu-view.ts — the HTML/DOM general-UI menu, VIEW half (HTML/DOM UI shell
// plan, Phase 2). Renders MenuController state to the DOM and routes clicks
// back to the controller; browser-only (the logic + launch boundary live in
// menu-controller.ts, which is node-tested). Faithful-FIRST styling: the same
// screens/controls/navigation as the canvas shell, recognizably the same dark
// layout — pixel-faithful art export (titlescreenbkgd.png, button bitmaps,
// the game fonts) is the later polish pass (plan open-question #2).
//
// When a launched game is running the menu hides itself and shows the canvas;
// otherwise the menu covers the window and the canvas is hidden.

import type { MenuController, MissionRow } from './menu-controller.ts';
import { ksfxGuiButtonTap, ksfxGuiCheckBoxTap } from '../ui/uikit.ts';
// (ksfxGuiButtonTap is also passed to the HELP manual for its Index/Back/link taps)
import { zipInStage } from './formchrome.ts';
import { type ButtonArt } from './title-art.ts';
import {
  COL_BG,
  COL_FG,
  COL_ACCENT,
  TEXT_SHADOW,
  el,
  formScale,
  FONT_BODY,
  buttonWidthU,
  makeShellButton,
  stageListBox,
  stageListItem,
  formTitle,
  ensureA11yStyles,
  centeredBand,
} from './chrome.ts';
import { mountHelpManual, type HelpManualHandle } from './help-manual-view.ts';

export interface MenuViewDeps {
  controller: MenuController;
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  showCanvas: () => void;
  hideCanvas: () => void;
  /** gszVersion, shown bottom-right like the canvas main menu's version label. */
  version: string;
  /** the title-screen backdrop (titlescreenbkgd.png) as a CSS url — the real
   *  artwork on the main menu. Absent ⇒ the text-title fallback look. */
  titleBg?: string;
  /** the WARFARE INCORPORATED wordmark (title.png) as an <img> src. */
  titleLogo?: string;
  /** the raw 9-slice button pieces; absent ⇒ CSS-button fallback. */
  buttonArt?: ButtonArt;
  /** play a GUI sound effect (ksfxGui*) — wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
}

// the agreed web-font approximation for button/label text (the original uses the
// pack's bitmap font; pixel-faithful glyph rendering was the not-chosen option)
const BTN_FONT = FONT_BODY;

// the shell ButtonControl height in design units — the main-menu/play buttons are
// authored 14u tall (forms.ini kidfStartup/kidfPlay); the sub-form buttons (options/
// load/missions) are 12u (kidfInGameOptions 1049 / kidfLoadGame 1038 / kidfSelectMission
// 1069). Keeping these as named constants so every screen sizes to the authored height.
const MENU_BTN_H = 14,
  SUB_BTN_H = 12;

export class MenuView {
  private readonly d: MenuViewDeps;
  /** the last screen rendered — the zip-in plays only on a screen CHANGE (a form
   *  open), not on intra-screen state updates (e.g. toggling an option). */
  private lastScreen: string | null = null;
  /** pending "form shown" SFX (fires at zip end; cleared if the screen changes first). */
  private formShowTimer: ReturnType<typeof setTimeout> | null = null;
  /** SelectMissionForm category tab (Story/Challenge) + the highlighted row. */
  private missionTab: MissionRow['bucket'] = 'story';
  private missionSel = 0; // index into missions() of the highlighted row
  private missionScroll = 0; // preserved mission-list scrollTop (selecting re-renders the list)
  private loadSel = -1; // the highlighted saved-game slot (LoadGameForm)
  /** the mounted full-fidelity HELP manual handle (Index/Back nav), live while on the help
   *  screen. Re-created on each helpScreen() render; the Index/Back buttons call into it. */
  private helpManual: HelpManualHandle | null = null;
  /** the shared 9-slice shell ButtonControl factory (chrome.ts) — owns its own
   *  composite cache + the up/down state swap + ksfxGuiButtonTap. */
  private readonly btn: (
    label: string,
    onClick: () => void,
    enabled?: boolean,
    size?: { w: number; h: number },
  ) => HTMLButtonElement;

  constructor(d: MenuViewDeps) {
    this.d = d;
    this.btn = makeShellButton(d, 'menu');
    ensureA11yStyles(); // the global keyboard :focus-visible ring (once)
    const s = d.root.style;
    s.position = 'fixed';
    s.inset = '0';
    s.display = 'flex';
    s.flexDirection = 'column';
    s.alignItems = 'center';
    s.justifyContent = 'center';
    s.background = COL_BG;
    s.color = COL_FG;
    s.fontFamily = 'system-ui, -apple-system, "Segoe UI", sans-serif';
    s.userSelect = 'none';
    s.zIndex = '10';
    s.overflow = 'hidden'; // clip controls parked off-window during the zip (no scrollbar flash)
    // the title screen is a fixed 240×160 form centered in the window; re-render
    // on resize so the design stage rescales (the canvas re-lays the form too).
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKeyDown);
    this.render();
  }

  // While another surface owns the screen (the multiplayer lobby + an online match,
  // DLC, community — each hides this root and re-shows it + render()s on exit) the menu
  // must stay inert: a resize (a soft keyboard opening for in-game chat, a rotation)
  // would otherwise render() the Play menu over the running match, since inGame is
  // only set for single-player launches.
  private suspended(): boolean {
    return this.d.root.style.display === 'none';
  }

  private readonly onResize = (): void => {
    if (this.suspended()) return;
    this.render();
  };

  // Escape → the current screen's BACK action (the SAME thing the on-screen BACK button
  // does), matching the in-game dialogs' Esc wiring (confirm-view.ts). On the main menu
  // there is no BACK, so Escape is a no-op (the canvas main form has no Cancel either).
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    // hidden (a single-player game, the lobby / an online match, DLC, community) — that
    // surface owns Esc. render() hides the root whenever inGame, so this covers it too.
    if (this.suspended()) return;
    const c = this.d.controller;
    switch (c.screen) {
      case 'play':
        e.preventDefault();
        c.back();
        break; // → main
      case 'missions':
        e.preventDefault();
        c.play();
        break; // → the play submenu
      case 'options':
        e.preventDefault();
        c.cancelOptions();
        break; // CANCEL (revert) → main
      case 'load':
        e.preventDefault();
        c.back();
        break; // → main
      case 'help':
        e.preventDefault();
        c.back();
        break; // MENU/BACK → main
      default:
        break; // 'main' — no BACK; Esc is a no-op
    }
  };

  /** detach the listeners (the menu lives for the page lifetime, but a re-created view
   *  must not leak listeners bound to the stale instance). */
  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKeyDown);
  }

  // ── the faithful design stage (forms.ini: the shell forms are 240×160) ───────
  // The canvas draws a ShellForm at a FIXED magnification (≈2 game px per design
  // unit), centered in the surface, then the surface scales by gameScale (1.5 for
  // the 24px/2432 desktop art — index.html:241). The magnification does NOT grow
  // with the window — measured: the 17-unit button row pitch is 51 CSS px at BOTH
  // 1024px AND 1920px wide ⇒ a constant 3.0 CSS px/unit (so the menu stays compact
  // on big windows, with more backdrop around it — NOT a viewport fraction). We
  // only shrink below that when a 240×160 form (+margin) won't fit a small window.
  // a wUnits×160 design stage centered in the window (the ShellForm coordinate
  // space). Most shell forms are 240 wide; the OPTIONS (kidfInGameOptions 1049) and
  // LOAD (kidfLoadGame 1038) forms are 160 wide, so they pass wUnits=160 — the scale
  // is the SAME (formScale's fixed 240-form fit budget), the narrower form just
  // centers with more backdrop either side, exactly like the canvas.
  private makeStage(wUnits = 240): { stage: HTMLElement; s: number } {
    const s = formScale();
    const stage = el('div', {
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: `${wUnits * s}px`,
      height: `${160 * s}px`,
      transform: 'translate(-50%, -50%)',
    });
    return { stage, s };
  }

  // position a child at forms.ini design coords (x, y[, w, h]) within the stage.
  private at(
    child: HTMLElement,
    s: number,
    x: number,
    y: number,
    w?: number,
    h?: number,
  ): HTMLElement {
    Object.assign(child.style, { position: 'absolute', left: `${x * s}px`, top: `${y * s}px` });
    if (w !== undefined) child.style.width = `${w * s}px`;
    if (h !== undefined) child.style.height = `${h * s}px`;
    return child;
  }

  // a 14-unit-tall stage button (the shell's standard ButtonControl height),
  // placed at forms.ini design coords (x, y) with a w-unit-wide composited bitmap.
  private stageButton(
    s: number,
    label: string,
    x: number,
    y: number,
    w: number,
    on: () => void,
    en = true,
    h = MENU_BTN_H,
  ): HTMLElement {
    return this.at(this.btn(label, on, en, { w: w * s, h: h * s }), s, x, y);
  }

  // a stage button SIZED to its own label (the cap-aware buttonWidthU) and CENTRED
  // horizontally within the [bandX, bandX+bandW] design band at row y. Used for the
  // single-column main-menu / play buttons where a uniform width would over-pad the
  // short labels (PLAY) or clip the long ones (LOAD SAVED GAME) — one size won't fit.
  private centredButton(
    s: number,
    label: string,
    y: number,
    on: () => void,
    en = true,
    bandX = 0,
    bandW = 240,
    h = MENU_BTN_H,
  ): HTMLElement {
    const w = buttonWidthU(label, h, s);
    return this.stageButton(s, label, bandX + (bandW - w) / 2, y, w, on, en, h);
  }

  // a stacked COLUMN of buttons at ONE UNIFORM width = the WIDEST label's measured width
  // (cap-aware buttonWidthU), each centred at its y. Used for the DELIBERATELY
  // consistent-width menu columns — the title screen, Play Game, and the Add-On chooser —
  // so they read as a clean column (uniform), sized to the widest so the longest label
  // still fits on one line (the "must fit the text" caveat).
  private uniformColumn(
    s: number,
    items: { label: string; y: number; on: () => void; en?: boolean }[],
    bandX = 0,
    bandW = 240,
    h = MENU_BTN_H,
  ): HTMLElement[] {
    const w = items.reduce((mx, it) => Math.max(mx, buttonWidthU(it.label, h, s)), 0);
    return items.map((it) =>
      this.stageButton(s, it.label, bandX + (bandW - w) / 2, it.y, w, it.on, it.en ?? true, h),
    );
  }

  // a centred ROW of buttons, packed with an even gap and centred within the
  // [bandX, bandX+bandW] band at row y (the OPTIONS|HELP split, the options
  // OK/CANCEL/DEFAULT, the mission/load PLAY/BACK pairs). Per-label by default so a short
  // label never forces a wide one to clip; `uniform` sizes them all to the widest (a
  // consistent-width pair, e.g. the title screen's OPTIONS|HELP).
  private buttonRow(
    s: number,
    y: number,
    on: { label: string; on: () => void; en?: boolean }[],
    bandX = 0,
    bandW = 240,
    h = MENU_BTN_H,
    gapU = 5,
    uniform = false,
  ): HTMLElement[] {
    const wMax = uniform ? on.reduce((mx, b) => Math.max(mx, buttonWidthU(b.label, h, s)), 0) : 0;
    const sized = on.map((b) => ({ ...b, w: uniform ? wMax : buttonWidthU(b.label, h, s) }));
    const totalW = sized.reduce((a, b) => a + b.w, 0) + gapU * (sized.length - 1);
    let x = bandX + Math.max(0, (bandW - totalW) / 2);
    const out: HTMLElement[] = [];
    for (const b of sized) {
      out.push(this.stageButton(s, b.label, x, y, b.w, b.on, b.en ?? true, h));
      x += b.w + gapU;
    }
    return out;
  }

  render(): void {
    const { controller, root } = this.d;
    if (controller.inGame) {
      root.style.display = 'none';
      this.lastScreen = null; // returning from a game re-opens the menu (re-zip)
      this.d.showCanvas();
      return;
    }
    this.d.hideCanvas();
    root.style.display = 'flex';
    // EVERY shell screen renders over the title backdrop: in the canvas all of
    // these forms extend ShellForm, whose OnPaintBackground (shellform.ts) draws
    // titlescreenbkgd.png centered with a black letterbox behind the controls.
    const onTitle = this.d.titleBg !== undefined;
    root.style.backgroundImage = onTitle ? `url(${this.d.titleBg})` : 'none';
    root.style.backgroundColor = COL_BG; // the letterbox fill around the backdrop
    root.style.backgroundSize = 'cover';
    root.style.backgroundPosition = 'center';
    root.style.backgroundRepeat = 'no-repeat';
    let screen: HTMLElement;
    switch (controller.screen) {
      case 'play':
        screen = this.playScreen();
        break;
      case 'missions':
        screen = this.missionScreen();
        break;
      case 'options':
        screen = this.optionsScreen();
        break;
      case 'help':
        screen = this.helpScreen();
        break;
      case 'load':
        screen = this.loadScreen();
        break;
      default:
        screen = this.mainScreen();
    }
    root.replaceChildren(screen);
    // restore the mission-list scroll: selecting a mission re-renders → a fresh list at
    // scrollTop 0, which would jump the view to the top + hide the just-clicked selection.
    // Preserve the position (the select handler captured it; tab-switch resets it to 0).
    const ml = root.querySelector('[data-mission-list]') as HTMLElement | null;
    if (ml !== null && this.missionScroll > 0) ml.scrollTop = this.missionScroll;
    // the version label is window-positioned (outside the stage transform). The canvas
    // shows it ONLY on the startup form (kidcVersion lives in kidfStartup, NOT kidfPlay),
    // so render it on 'main' only — it was leaking onto the play submenu.
    if (controller.screen === 'main') root.append(this.windowVersionLabel());

    const changed = controller.screen !== this.lastScreen;
    this.lastScreen = controller.screen;
    if (changed) this.zipIn(screen);
  }

  // the ShellForm zip-in (formchrome.ts zipInStage ↔ shellform.ts:298-384): EVERY
  // control on the form cascades to its place from an alternating side (odd→left,
  // even→right), staggered, then ksfxGuiFormShow plays. The mission/room LIST is one
  // control here so it zips as a single unit; the backdrop/wordmark is on the root
  // (not a stage child) so it doesn't move.
  private zipIn(screen: HTMLElement): void {
    if (this.formShowTimer !== null) clearTimeout(this.formShowTimer);
    this.formShowTimer = zipInStage(screen, this.d.playSfx);
  }

  // the main menu (forms.ini kidfStartup 1021 — a 240×160 form, controls placed at
  // their authored coords): the wordmark, the button column, the OPTIONS|HELP
  // split row, copyright + version — scaled to the window like the canvas form.
  private mainScreen(): HTMLElement {
    const c = this.d.controller;
    const { stage, s } = this.makeStage();
    stage.append(this.at(this.wordmarkImg(), s, 28, 7, 184)); // title.rbm (28,7), centered span
    // the main-menu buttons are a DELIBERATELY consistent-width column (the canvas look):
    // ONE uniform width = the widest label ("LOAD SAVED GAME"), so they read as a clean
    // column and the longest still fits on one line. (Per-label here made them ragged.)
    // Four entries (COMMUNITY absorbed the LEADERBOARD + MY PROFILE pair as tabs), back on the
    // original 17u pitch. Column start (59) is unchanged so the wordmark clearance is untouched.
    const colItems = [
      { label: 'PLAY', y: 59, on: () => c.play() },
      { label: 'COMMUNITY', y: 76, on: () => c.showCommunity(), en: c.hasCommunity() },
      { label: 'LOAD SAVED GAME', y: 93, on: () => c.load(), en: c.hasSaves() },
      { label: 'MISSION PACKS', y: 110, on: () => c.showAddOnPacks(), en: c.hasAddOnPacks() },
    ];
    this.uniformColumn(s, colItems).forEach((b) => stage.append(b));
    // web build: FORUMS hidden, OPTIONS+HELP split the bottom row. They span EXACTLY the
    // column above — OPTIONS' left aligns with the column's left edge, HELP's right with
    // its right edge — so each half is (colW − gap)/2 (a smidge narrower than its natural
    // label width), rather than the natural-width pair centred in the full 240 band.
    const colW = colItems.reduce(
      (mx, it) => Math.max(mx, buttonWidthU(it.label, MENU_BTN_H, s)),
      0,
    );
    const colLeft = (240 - colW) / 2,
      splitGapU = 5,
      halfW = (colW - splitGapU) / 2;
    stage.append(
      this.stageButton(s, 'OPTIONS', colLeft, 135, halfW, () => c.options(), c.hasOptions()),
    );
    stage.append(
      this.stageButton(
        s,
        'HELP',
        colLeft + halfW + splitGapU,
        135,
        halfW,
        () => c.help(),
        c.hasHelp(),
      ),
    );
    stage.append(this.at(this.copyrightLabel(s), s, 52, 151, 136));
    return stage;
  }

  // PLAY → the play submenu (forms.ini kidfPlay 1043): a "PLAY GAME" title, SINGLE
  // PLAYER + MULTIPLAYER full buttons, BACK at the bottom-right — same 240×160.
  private playScreen(): HTMLElement {
    const c = this.d.controller;
    const { stage, s } = this.makeStage();
    stage.append(this.at(formTitle('PLAY GAME', s), s, 2, 6, 236));
    // SINGLE PLAYER / MULTIPLAYER — a UNIFORM-width pair (both = the widest, "SINGLE
    // PLAYER"), the deliberate consistent-width look + matching the Add-On chooser.
    this.uniformColumn(s, [
      { label: 'SINGLE PLAYER', y: 42, on: () => c.singlePlayer() },
      { label: 'MULTIPLAYER', y: 59, on: () => c.multiplayer(), en: c.hasMultiplayer() },
    ]).forEach((b) => stage.append(b));
    stage.append(this.centredButton(s, 'BACK', 140, () => c.back()));
    return stage;
  }

  // the WARFARE INCORPORATED wordmark (title.png) as an <img>; text fallback.
  private wordmarkImg(): HTMLElement {
    if (this.d.titleLogo !== undefined) {
      const logo = document.createElement('img');
      logo.src = this.d.titleLogo;
      logo.alt = 'Warfare Incorporated';
      Object.assign(logo.style, { height: 'auto', imageRendering: 'pixelated' });
      return logo;
    }
    const h = el('div', {
      letterSpacing: '0.1em',
      fontSize: '5vw',
      color: COL_ACCENT,
      textAlign: 'center',
      fontWeight: '700',
    });
    h.textContent = 'WARFARE INCORPORATED';
    return h;
  }

  // copyright.rbm (52,148) — rendered as centered text (the web font approximation).
  // A drop shadow (the shared TEXT_SHADOW token) lifts it off the title backdrop so the
  // thin grey line pops, matching the legibility shadow on the other shell labels.
  private copyrightLabel(s: number): HTMLElement {
    const cr = el('div', {
      opacity: '0.7',
      fontSize: `${Math.round(4.5 * s)}px`,
      textAlign: 'center',
      fontFamily: BTN_FONT,
      whiteSpace: 'nowrap',
      textShadow: TEXT_SHADOW,
    });
    cr.textContent = 'Copyright © 2003-2026 Spiffcode, Inc';
    return cr;
  }

  // the gszVersion label (1011) — at the WINDOW's bottom-right corner (matrix:
  // positioned against the surface, not the form), so it sits outside the stage
  // transform. Appended to root in render(), not into the stage.
  private windowVersionLabel(): HTMLElement {
    const ver = el('div', {
      position: 'fixed',
      opacity: '0.6',
      fontSize: '12px',
      fontFamily: BTN_FONT,
      // safe-area insets so the label clears the home indicator / notch on a standalone PWA
      right: 'calc(10px + env(safe-area-inset-right, 0px))',
      bottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
    });
    ver.textContent = `v${this.d.version}`;
    return ver;
  }

  // SelectMissionForm (forms.ini kidfSelectMissionWide 1069, a 240×160 form): title
  // "PLAY SINGLE PLAYER MISSION" (0,6,240) center; the Story|Challenge|Add-On tab bar
  // (RADIOBUTTONBAR 0,19); the per-category mission LIST (2,33,236,93); the info LABEL
  // (2,127,236,12); the empty-Add-On message (2,114,236) center; PLAY (71,141,40,12) +
  // BACK (126,141). On the SAME 240-design stage as the other shell screens (was a
  // separate fixed-px flex layout) so it renders at the IDENTICAL scale.
  private missionScreen(): HTMLElement {
    const c = this.d.controller;
    const rows = c.missions();
    const { stage, s } = this.makeStage();
    // the canvas SelectMissionForm always shows all three category tabs
    const buckets: MissionRow['bucket'][] = ['story', 'challenge', 'addon'];
    const inTab = rows.filter((r) => r.bucket === this.missionTab);
    if (!inTab.some((r) => r.index === this.missionSel)) this.missionSel = inTab[0]?.index ?? -1;
    const tabName: Record<MissionRow['bucket'], string> = {
      story: 'Story',
      challenge: 'Challenge',
      addon: 'Add-On',
    };

    stage.append(this.at(formTitle('PLAY SINGLE PLAYER MISSION', s), s, 0, 6, 240));

    // category tabs "Story | Challenge | Add-On" (RADIOBUTTONBAR 0,19) — a centred
    // row of segments, separators between, the active one underlined (the canvas look).
    const tabs = el('div', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0',
      whiteSpace: 'nowrap',
    });
    tabs.setAttribute('role', 'radiogroup'); // the RADIOBUTTONBAR → a radiogroup (a11y, visual-neutral)
    tabs.setAttribute('aria-label', 'Mission category');
    buckets.forEach((b, i) => {
      if (i > 0) {
        const sep = el('span', { color: 'rgba(232,238,240,0.4)', padding: `0 ${4 * s}px` });
        sep.textContent = '|';
        sep.setAttribute('aria-hidden', 'true');
        tabs.append(sep);
      }
      const active = b === this.missionTab;
      const t = el('button', {
        font: 'inherit',
        fontFamily: BTN_FONT,
        fontSize: `${Math.round(6 * s)}px`,
        letterSpacing: '0.04em',
        padding: `${1 * s}px ${1.5 * s}px`,
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        color: COL_FG,
        textDecoration: active ? 'underline' : 'none',
        textUnderlineOffset: `${1.5 * s}px`,
        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
      });
      t.textContent = tabName[b];
      t.setAttribute('role', 'radio');
      t.setAttribute('aria-checked', active ? 'true' : 'false');
      t.addEventListener('click', () => {
        this.d.playSfx?.(ksfxGuiButtonTap);
        this.missionTab = b;
        this.missionSel = -1;
        this.missionScroll = 0;
        this.render();
      });
      tabs.append(t);
    });
    stage.append(this.at(tabs, s, 0, 19, 240));

    // the mission LIST (2,33,236,93) — a fixed box; switching tabs never moves the
    // title/tabs/caption/buttons. An empty Add-On shows its message centred inside.
    const list = stageListBox(s);
    list.dataset.missionList = '1'; // tagged so render() can preserve its scroll across re-renders
    list.setAttribute('aria-label', 'Missions'); // a11y name for the listbox (the visible title isn't a label el)
    if (inTab.length === 0) {
      // empty Add-On — kidcAddOnMessage, centred in the box
      list.style.alignItems = 'center';
      list.style.justifyContent = 'center';
      const note = el('div', {
        ...stageLabelStyle(s),
        opacity: '0.9',
        textAlign: 'center',
        padding: `0 ${6 * s}px`,
      });
      note.textContent = 'Install Add-On Mission Packs from the Main Menu';
      list.append(note);
    } else {
      for (const row of inTab) {
        // the authored title already carries its number ("M0: Introduction") — show as-is.
        list.append(
          stageListItem(row.title || row.lvl, row.index === this.missionSel, s, {
            onSelect: () => {
              this.missionScroll = list.scrollTop;
              this.missionSel = row.index;
              this.render();
            },
            onActivate: () => c.launch(row.index),
            playSfx: this.d.playSfx,
          }),
        );
      }
    }
    // portrait boost: the wide 236u mission list reflows into the centered band so it
    // stays on-screen (taller rows / bigger text at the same ~full width); no-op otherwise.
    const listB = centeredBand(2, 236);
    stage.append(this.at(list, s, listB.x, 33, listB.w, 93));

    // the info label "<packName> by <author>" (2,127,236). Community Add-On packs
    // carry the publisher's name (wi/info `a`); the main game's own missions have no
    // pack author, so they keep the classic "by Spiffcode, Inc." byline.
    const cap = el('div', {
      ...stageLabelStyle(s),
      opacity: '0.7',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });
    cap.textContent =
      inTab.length > 0 ? `${inTab[0].pack} by ${inTab[0].author || 'Spiffcode, Inc.'}` : '';
    stage.append(this.at(cap, s, listB.x, 127, listB.w, 12));

    // PLAY only when the tab has playable missions (the canvas hides it on empty
    // Add-On, leaving BACK centred) + BACK → the PLAY GAME submenu. Sized per-label.
    const btns: { label: string; on: () => void; en?: boolean }[] = [];
    if (inTab.length > 0)
      btns.push({
        label: 'PLAY',
        on: () => {
          if (this.missionSel >= 0) c.launch(this.missionSel);
        },
        en: this.missionSel >= 0,
      });
    btns.push({ label: 'BACK', on: () => c.play() });
    this.buttonRow(s, 141, btns, 0, 240, SUB_BTN_H).forEach((b) => stage.append(b));
    return stage;
  }

  // GameOptionsForm (forms.ini kidfInGameOptions 1049, a 160-WIDE form): title
  // "GAME OPTIONS" (38,6,83) center; "Game Speed" (11,26) + slider (11,40,95,8) +
  // value (121,40,17) right; "Scroll Speed" (11,56) + slider (11,70,95,8) + value
  // (121,70,17) right; "Difficulty" (11,89) + Easy/Normal/Hard radios (11,99/109/119,
  // 67); "Miscellaneous" (80,89) + Mute Sound (80,99) / Mouse Input (80,109, forced
  // on + disabled on web); OK (13,140,40,12) / CANCEL (60,140) / DEFAULT (107,140).
  // On the SAME 160-design stage the in-game GameOptionsForm uses (game-options-view.ts)
  // so the shell + in-game options forms render at the IDENTICAL scale; OK/CANCEL/DEFAULT
  // are sized PER-LABEL + centred (3 uniform DEFAULT-widths won't fit a 160u form).
  private optionsScreen(): HTMLElement {
    const c = this.d.controller;
    const { stage, s } = this.makeStage(160);
    stage.append(this.at(formTitle('GAME OPTIONS', s), s, 0, 6, 160));

    // Game Speed: label (11,26) + slider (11,40,95,8) + value (121,40) right. The
    // value box is widened 17u → 24u (x 121 → 116) so the wide WI font shows the full
    // "1.0x"/"11.0x" readout without clipping (matching game-options-view).
    stage.append(this.at(this.stageLabel('Game Speed', 'left', s), s, 11, 26, 68));
    const gMax = c.gameSpeedNotchMax();
    const gsVal = this.stageValue(c.gameSpeedLabel(), s);
    stage.append(
      this.at(
        this.stageSlider(
          gMax > 0 ? c.gameSpeedNotch() / gMax : 0,
          s,
          (f) => {
            c.setGameSpeedNotch(Math.round(f * gMax));
            return { frac: gMax > 0 ? c.gameSpeedNotch() / gMax : 0, label: c.gameSpeedLabel() };
          },
          (lbl) => {
            gsVal.textContent = lbl;
          },
          'Game Speed',
          c.gameSpeedLabel(),
          gMax,
        ),
        s,
        11,
        40,
        95,
        8,
      ),
    );
    stage.append(this.at(gsVal, s, 116, 40, 24));

    // Scroll Speed: label (11,56) + slider (11,70,95,8) + value (121,70) right. The
    // controller exposes scroll as 1.0..5.0 in .25 steps → 0..16 notches.
    stage.append(this.at(this.stageLabel('Scroll Speed', 'left', s), s, 11, 56, 68));
    const ssVal = this.stageValue(c.scrollSpeedLabel(), s);
    stage.append(
      this.at(
        this.stageSlider(
          (c.scrollSpeed() - 1) / 4,
          s,
          (f) => {
            c.setScrollSpeed(1 + Math.round(f * 16) * 0.25);
            return { frac: (c.scrollSpeed() - 1) / 4, label: c.scrollSpeedLabel() };
          },
          (lbl) => {
            ssVal.textContent = lbl;
          },
          'Scroll Speed',
          c.scrollSpeedLabel(),
          16,
        ),
        s,
        11,
        70,
        95,
        8,
      ),
    );
    stage.append(this.at(ssVal, s, 116, 70, 24));

    // Difficulty (11,89) + the 3 mutually-exclusive radios (the square-check look). The
    // three radios stay DIRECT stage children (so the zip cascade is unchanged); a
    // layout-neutral aria-owns radiogroup ties them together for a screen reader.
    const diffLabel = this.at(this.stageLabel('Difficulty', 'left', s), s, 11, 89, 67);
    stage.append(diffLabel);
    const diffs: [string, 'easy' | 'normal' | 'hard', number][] = [
      ['Easy', 'easy', 99],
      ['Normal', 'normal', 109],
      ['Hard', 'hard', 119],
    ];
    const diffIds: string[] = [];
    diffs.forEach(([text, d, y], i) => {
      const radio = this.at(
        this.stageCheck(text, c.difficulty() === d, () => c.setDifficulty(d), false, s, 'radio'),
        s,
        11,
        y,
        67,
      );
      radio.id = `wi-diff-${i}`;
      diffIds.push(radio.id);
      stage.append(radio);
    });
    // a11y-only: a display:none radiogroup owning the three radios via aria-owns — gives
    // the radios a group + name for assistive tech. NESTED inside the (already-present)
    // Difficulty label so it is NOT a separate stage child (the zip cascades the stage's
    // direct children, so it must not add one). No box (display:none) ⇒ no layout impact.
    const diffGroup = el('div', { display: 'none' });
    diffGroup.setAttribute('role', 'radiogroup');
    diffGroup.setAttribute('aria-label', 'Difficulty');
    diffGroup.setAttribute('aria-owns', diffIds.join(' '));
    diffLabel.append(diffGroup);

    // Miscellaneous (80,89) + Mute Sound (80,99) + Mouse Input (80,109). Web no longer
    // forces Mouse Input (PR #124 — touch devices default to the FingerHandler), so it
    // reflects the real pref + is toggleable; OK persists it (Lasso Selection hidden on web).
    stage.append(this.at(this.stageLabel('Miscellaneous', 'left', s), s, 80, 89, 68));
    stage.append(
      this.at(
        this.stageCheck(
          'Mute Sound',
          c.muted(),
          () => c.setMuted(!c.muted()),
          false,
          s,
          'checkbox',
        ),
        s,
        80,
        99,
        67,
      ),
    );
    stage.append(
      this.at(
        this.stageCheck(
          'Mouse Input',
          c.mouseMode(),
          () => c.setMouseMode(!c.mouseMode()),
          false,
          s,
          'checkbox',
        ),
        s,
        80,
        109,
        67,
      ),
    );

    // OK / CANCEL / DEFAULT — sized PER-LABEL (cap-aware) + centred on the 160-form.
    // DEFAULT (gameoptions.ts:502-509): normal difficulty, unmuted, default game speed,
    // scroll 1.0x — quiet setters, so re-render to refresh (it does NOT close).
    this.buttonRow(
      s,
      140,
      [
        { label: 'OK', on: () => c.saveOptions() },
        { label: 'CANCEL', on: () => c.cancelOptions() },
        {
          label: 'DEFAULT',
          on: () => {
            c.setDifficulty('normal');
            c.setMuted(false);
            c.setGameSpeedNotch(c.defaultGameSpeedNotch());
            c.setScrollSpeed(1);
            this.render();
          },
        },
      ],
      0,
      160,
      SUB_BTN_H,
      4,
    ).forEach((b) => stage.append(b));
    return stage;
  }

  // HelpForm (forms.ini kidfHelpWide 1072, a 240×160 form): the HELP text region
  // (5,6,228,130) + the C++ kidcIndex/kidcBack/kidcNextPage/kidcPrevPage/kidcOk buttons.
  // The DOM version renders the FULL-FIDELITY manual (help-manual-view.ts) in that region —
  // inline images, clickable cross-links, anchors — with INDEX + (internal) BACK reproduced
  // faithfully; the single scrollable doc replaces NextPage/PrevPage (the DOM idiom). A
  // distinct MENU button exits the manual (the canvas kidcOk). On the SAME 240-design stage
  // as the other shell screens, so it renders at the IDENTICAL scale.
  private helpScreen(): HTMLElement {
    const c = this.d.controller;
    const { stage, s } = this.makeStage();
    stage.append(this.at(formTitle('HELP', s), s, 0, 6, 240));

    // the manual body region (the authored HELP region, below the title at y=20). A host
    // div the manual mounts into; [data-list] excludes it from the form zip.
    const host = el('div', { position: 'absolute', inset: '0', overflow: 'hidden' });
    host.dataset.list = '1';
    // portrait boost: reflow the manual body into the centered band (no-op otherwise).
    const helpB = centeredBand(5, 230);
    const region = this.at(host, s, helpB.x, 20, helpB.w, 116);

    if (c.hasHelpManual()) {
      // the rich manual (RAW help.txt → images / cross-links / Index / Back). onNavChange
      // re-renders the nav button row so the in-doc BACK enables/disables as the history grows
      // — fired by link clicks too, not just the Index/Back buttons.
      this.helpManual = mountHelpManual(host, s, {
        raw: c.helpRaw(),
        resolveImage: (src) => c.resolveHelpImage(src),
        playSfx: this.d.playSfx,
        sfxTap: ksfxGuiButtonTap,
        onNavChange: () => this.refreshHelpButtons(stage, s),
      });
    } else {
      // fallback: the flat tag-stripped text (no RAW source wired, e.g. the node harness).
      this.helpManual = null;
      const body = el('div', {
        overflowY: 'auto',
        whiteSpace: 'pre-wrap',
        lineHeight: '1.4',
        fontFamily: BTN_FONT,
        fontSize: `${Math.round(5.5 * s)}px`,
        color: COL_FG,
        textShadow: '0 1px 2px rgba(0,0,0,0.9)',
        background: 'rgba(7,22,27,0.55)',
        border: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.35)`,
        boxSizing: 'border-box',
        padding: `${2 * s}px ${4 * s}px`,
        width: '100%',
        height: '100%',
      });
      body.dataset.list = '1';
      body.textContent = c.helpContent() || 'Help unavailable.';
      host.append(body);
    }
    stage.append(region);

    // the bottom button row. With the rich manual: INDEX (kidcIndex → top) + BACK (kidcBack →
    // pop the in-doc history) + MENU (kidcOk → leave the manual) — built by refreshHelpButtons
    // (which is also the manual's onNavChange handler, so the in-doc BACK enables once there's
    // somewhere to go back to). Without it: just BACK-to-menu (the legacy single button).
    if (this.helpManual !== null) {
      this.refreshHelpButtons(stage, s);
    } else {
      const back = this.centredButton(s, 'BACK', 141, () => c.back(), true, 0, 240, SUB_BTN_H);
      stage.append(back);
    }
    return stage;
  }

  // (re)render just the help nav button row so the in-doc BACK reflects canGoBack() after an
  // Index/Back/link jump (the manual body itself is untouched — its scroll is view state). The
  // m.toIndex()/m.goBack() handlers fire the manual's onNavChange → this, so the row stays in
  // sync with the history without a controller round-trip.
  private refreshHelpButtons(stage: HTMLElement, s: number): void {
    const m = this.helpManual;
    if (m === null) return;
    stage.querySelectorAll('[data-help-nav]').forEach((n) => n.remove());
    this.buttonRow(
      s,
      141,
      [
        { label: 'INDEX', on: () => m.toIndex() },
        { label: 'BACK', on: () => m.goBack(), en: m.canGoBack() },
        { label: 'MENU', on: () => this.d.controller.back() },
      ],
      0,
      240,
      SUB_BTN_H,
      4,
    ).forEach((b) => {
      b.dataset.helpNav = '1';
      stage.append(b);
    });
  }

  // LoadGameForm (forms.ini kidfLoadGame 1038, a 160×160 form): title LOAD SAVED GAME
  // (38,6,84) center; the slot LIST (3,19,153,115) in SLOT ORDER (0..N), each
  // "HH:MM <name>" or "-- Empty --", default-selecting the MOST-RECENTLY-SAVED slot
  // (loadsave.cpp m_nGameLast); LOAD (31,140,40,12) + BACK (86,140,40,12). On the SAME
  // 160-design stage as the in-game forms (was a fixed-px flex layout) — identical scale.
  private loadScreen(): HTMLElement {
    const c = this.d.controller;
    const { stage, s } = this.makeStage(160);
    stage.append(this.at(formTitle('LOAD SAVED GAME', s), s, 0, 6, 160));

    const saves = c.savedGames();
    const bySlot = new Map(saves.map((sv) => [sv.slot, sv]));
    // most-recently-saved slot (loadsave.cpp m_nGameLast) → default selection
    const recent = saves.reduce<(typeof saves)[number] | null>(
      (a, b) => (a === null || b.whenMs > a.whenMs ? b : a),
      null,
    );
    const maxSavedSlot = saves.reduce((m, sv) => Math.max(m, sv.slot), -1);
    const NSLOTS = Math.max(10, maxSavedSlot + 1); // ≈ the canvas cEntries (rows that fit)
    if (this.loadSel < 0 || !(this.loadSel < NSLOTS)) this.loadSel = recent ? recent.slot : 0;

    // the slot LIST (3,19,153,115) — fixed box; scrolls when the slots overflow.
    const list = stageListBox(s);
    list.setAttribute('aria-label', 'Saved games'); // a11y name for the listbox
    for (let i = 0; i < NSLOTS; i++) {
      const sv = bySlot.get(i);
      const text = sv ? `${formatWhen(sv.whenMs)}  ${sv.name || `Slot ${i}`}` : '-- Empty --';
      list.append(
        stageListItem(text, i === this.loadSel, s, {
          onSelect: () => {
            this.loadSel = i;
            this.render();
          },
          onActivate: () => c.loadSlot(i),
          playSfx: this.d.playSfx,
        }),
      );
    }
    stage.append(this.at(list, s, 3, 19, 153, 115));

    // LOAD (always enabled, like the canvas; loadSlot no-ops on an empty slot) + BACK,
    // each sized per-label + centred on the 160-form.
    this.buttonRow(
      s,
      140,
      [
        { label: 'LOAD', on: () => c.loadSlot(this.loadSel) },
        { label: 'BACK', on: () => c.back() },
      ],
      0,
      160,
      SUB_BTN_H,
    ).forEach((b) => stage.append(b));
    return stage;
  }

  // a single-line stage LABEL (the canvas LabelControl), left- or right-aligned,
  // sized to fill its at() box. Used for the options field labels + slider readouts.
  private stageLabel(text: string, align: 'left' | 'right', s: number): HTMLElement {
    const e = el('div', {
      ...stageLabelStyle(s),
      textAlign: align,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });
    e.textContent = text;
    return e;
  }

  // the slider VALUE readout (the N.Nx label at the right of each slider row).
  private stageValue(text: string, s: number): HTMLElement {
    return this.stageLabel(text, 'right', s);
  }

  // the canvas RadioButton/CheckBox look: a square outlined box with an X when set,
  // a label to the right (the difficulty radios AND the misc checkboxes draw this).
  // Single- vs multi-select is the caller's onToggle; `disabled` greys + no-ops.
  // Scaled to the design stage (mirrors game-options-view's squareCheck).
  // `role` ('radio' for the mutually-exclusive difficulty, 'checkbox' for Mute/Mouse)
  // is a11y-only — it adds aria-checked + aria-disabled with NO rendered change.
  private stageCheck(
    label: string,
    checked: boolean,
    onToggle: () => void,
    disabled: boolean,
    s: number,
    role: 'radio' | 'checkbox' = 'checkbox',
  ): HTMLElement {
    const bx = Math.round(7 * s);
    const row = el('label', {
      display: 'flex',
      alignItems: 'center',
      gap: `${Math.round(3 * s)}px`,
      fontFamily: BTN_FONT,
      fontSize: `${Math.round(5.5 * s)}px`,
      color: COL_FG,
      textShadow: '0 1px 2px rgba(0,0,0,0.8)',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? '0.75' : '1',
      whiteSpace: 'nowrap',
    });
    const box = el('span', {
      width: `${bx}px`,
      height: `${bx}px`,
      border: `${Math.max(1, Math.round(s))}px solid ${COL_ACCENT}`,
      position: 'relative',
      flex: '0 0 auto',
      boxSizing: 'border-box',
    });
    if (checked) {
      const x = COL_ACCENT;
      box.append(
        el('span', {
          position: 'absolute',
          inset: `${Math.max(1, Math.round(s * 0.5))}px`,
          background: `linear-gradient(45deg, transparent 41%, ${x} 41%, ${x} 59%, transparent 59%), linear-gradient(-45deg, transparent 41%, ${x} 41%, ${x} 59%, transparent 59%)`,
        }),
      );
    }
    const t = el('span', {});
    t.textContent = label;
    row.append(box, t);
    // a11y: a <label> isn't a focusable/announced control by itself — expose it as a
    // radio/checkbox with its checked state + name (visual-neutral). tabindex makes it
    // keyboard-reachable (Tab + Enter/Space toggles via the click handler below).
    row.setAttribute('role', role);
    row.setAttribute('aria-checked', checked ? 'true' : 'false');
    row.setAttribute('aria-label', label);
    if (disabled) row.setAttribute('aria-disabled', 'true');
    else row.tabIndex = 0;
    if (!disabled) {
      row.addEventListener('pointerdown', (e) => {
        e.preventDefault();
      }); // mousedown starts the selection — suppress it
      row.addEventListener('click', (e) => {
        e.preventDefault();
        this.d.playSfx?.(ksfxGuiCheckBoxTap);
        onToggle();
      });
      // Enter / Space activate the focused control (the native button-like behavior a
      // <label role=radio> doesn't get for free) — keyboard parity with a click.
      row.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.d.playSfx?.(ksfxGuiCheckBoxTap);
          onToggle();
        }
      });
    }
    return row;
  }

  // a horizontal SLIDER (the canvas SliderControl: a thin track + a small thumb)
  // sized to fill its at() box. `quantize` applies the dragged fraction to the
  // controller and returns the snapped fraction + label; `onLabel` updates the row's
  // value readout during the drag (the controller setter is quiet — no mid-drag
  // re-render). Scaled to the design stage (mirrors game-options-view's slider).
  private stageSlider(
    initialFrac: number,
    s: number,
    quantize: (f: number) => { frac: number; label: string },
    onLabel: (label: string) => void,
    ariaLabel?: string,
    initialLabel?: string,
    notches?: number,
  ): HTMLElement {
    const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
    const track = el('div', {
      position: 'relative',
      height: '100%',
      width: '100%',
      cursor: 'pointer',
    });
    track.append(
      el('div', {
        position: 'absolute',
        left: '0',
        right: '0',
        top: '50%',
        height: `${Math.max(1, Math.round(s))}px`,
        transform: 'translateY(-50%)',
        background: 'rgba(96,156,214,0.95)',
      }),
    );
    const thumb = el('div', {
      position: 'absolute',
      top: '50%',
      width: `${Math.max(2, Math.round(3 * s))}px`,
      height: '100%',
      transform: 'translate(-50%, -50%)',
      background: COL_ACCENT,
      left: `${clamp01(initialFrac) * 100}%`,
      boxShadow: '0 0 4px rgba(0,0,0,0.6)',
    });
    track.append(thumb);
    // a11y: the bare-<div> SliderControl → role=slider with the standard value props.
    // aria-valuemin/max are notch indices (0..notches) when known, else the 0..1 fraction;
    // aria-valuenow/valuetext track the live thumb position + the human readout (e.g. "1.0x").
    // Visual-neutral; tabindex makes it keyboard-reachable (arrows nudge it below).
    track.setAttribute('role', 'slider');
    if (ariaLabel !== undefined) track.setAttribute('aria-label', ariaLabel);
    const span = notches !== undefined && notches > 0 ? notches : 1;
    track.setAttribute('aria-valuemin', '0');
    track.setAttribute('aria-valuemax', String(span));
    track.tabIndex = 0;
    const setAria = (frac: number, label: string): void => {
      track.setAttribute('aria-valuenow', String(Math.round(clamp01(frac) * span)));
      track.setAttribute('aria-valuetext', label);
    };
    setAria(initialFrac, initialLabel ?? '');
    const commit = (frac: number): void => {
      const q = quantize(clamp01(frac));
      thumb.style.left = `${clamp01(q.frac) * 100}%`;
      onLabel(q.label);
      setAria(q.frac, q.label);
    };
    const apply = (clientX: number): void => {
      const r = track.getBoundingClientRect();
      commit((clientX - r.left) / r.width);
    };
    let dragging = false;
    track.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      dragging = true;
      this.d.playSfx?.(ksfxGuiButtonTap);
      track.setPointerCapture(e.pointerId);
      apply(e.clientX);
    });
    track.addEventListener('pointermove', (e) => {
      if (dragging) apply(e.clientX);
    });
    const end = (e: PointerEvent): void => {
      if (!dragging) return;
      dragging = false;
      try {
        track.releasePointerCapture(e.pointerId);
      } catch {
        /* capture already gone */
      }
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    // keyboard: ←/→ (and ↑/↓) nudge by one notch (or 5% when the notch count is unknown).
    track.addEventListener('keydown', (e) => {
      const dir =
        e.key === 'ArrowRight' || e.key === 'ArrowUp'
          ? 1
          : e.key === 'ArrowLeft' || e.key === 'ArrowDown'
            ? -1
            : 0;
      if (dir === 0) return;
      e.preventDefault();
      this.d.playSfx?.(ksfxGuiButtonTap);
      const now = Number(track.getAttribute('aria-valuenow') ?? '0');
      commit((now + dir) / span);
    });
    return track;
  }
}

// the shared design-stage label text style (scaled to the stage) — the options
// field labels, the mission caption/empty-message, the slider readouts.
function stageLabelStyle(s: number): Partial<CSSStyleDeclaration> {
  return {
    fontFamily: FONT_BODY,
    fontSize: `${Math.round(5.5 * s)}px`,
    color: COL_FG,
    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
  };
}

// save-slot timestamp → HH:MM (the canvas LoadGameForm shows the same)
function pad2(n: number): string {
  return String(n).padStart(2, '0');
}
function formatWhen(ms: number): string {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
